import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Alert, Button, Chip, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid,
  Card, CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  MonetizationOn as CashbackIcon,
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import cashbackService, {
  CashbackDashboard,
  CashbackCampaign,
  CashbackRecord,
  CampaignFormData,
} from '../../services/cashback.service';

// ─── Helpers ─────────────────────────────────────────────────

const formatCurrency = (v: number) =>
  `${Math.round(v || 0).toLocaleString('pt-PT')}$`;

const formatDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
// start_date/end_date são hora de parede de CV marcada com Z → mostrar em UTC.
const formatDateParede = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-CV', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const statusColor: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  ATIVA: 'success',
  RASCUNHO: 'default',
  PAUSADA: 'warning',
  TERMINADA: 'info',
  ESGOTADA: 'error',
  CONFIRMADO: 'success',
  PENDENTE: 'warning',
  EXPIRADO: 'error',
  CANCELADO: 'error',
  UTILIZADO: 'info',
};

const emptyForm: CampaignFormData = {
  name: '',
  description: '',
  tipo: 'PERCENTUAL',
  percentagem: 5,
  valor_fixo: undefined,
  max_por_transacao: 500,
  max_por_utilizador: undefined,
  max_utilizacoes_por_user: undefined,
  // Campo de parede: pré-preencher com a hora de CV, não a UTC.
  start_date: new Date(Date.now() - 3600e3).toISOString().slice(0, 16),
  end_date: new Date(Date.now() - 3600e3 + 30 * 86400000).toISOString().slice(0, 16),
  budget_total: 100000,
  funding_source: 'PAGALI',
  funding_split_pagali: 100,
  funding_split_merchant: 0,
  restricao_uso: 'LIVRE',
};

// ─── Component ───────────────────────────────────────────────

const CashbackPage: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dashboard
  const [dashboard, setDashboard] = useState<CashbackDashboard | null>(null);

  // Campaigns
  const [campaigns, setCampaigns] = useState<CashbackCampaign[]>([]);
  const [campaignTotal, setCampaignTotal] = useState(0);
  const [campaignPage, setCampaignPage] = useState(0);
  const [campaignRows, setCampaignRows] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // Report
  const [report, setReport] = useState<CashbackRecord[]>([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportPage, setReportPage] = useState(0);
  const [reportRows, setReportRows] = useState(25);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportEstado, setReportEstado] = useState('');

  // Form dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CampaignFormData>({ ...emptyForm });

  const [loading, setLoading] = useState(false);

  // ─── Data fetching ───────────────────────────────────────

  const fetchDashboard = useCallback(async () => {
    try {
      const data = await cashbackService.getDashboard();
      setDashboard(data);
    } catch (e: any) {
      console.error(e);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const result = await cashbackService.listCampaigns({
        status: statusFilter || undefined,
        search: search || undefined,
        limit: campaignRows,
        offset: campaignPage * campaignRows,
      });
      setCampaigns(result.data);
      setCampaignTotal(result.pagination.total);
    } catch (e: any) {
      console.error(e);
    }
  }, [statusFilter, search, campaignPage, campaignRows]);

  const fetchReport = useCallback(async () => {
    try {
      const result = await cashbackService.getReport({
        start_date: reportStartDate || undefined,
        end_date: reportEndDate || undefined,
        estado: reportEstado || undefined,
        limit: reportRows,
        offset: reportPage * reportRows,
      });
      setReport(result.data);
      setReportTotal(result.pagination.total);
    } catch (e: any) {
      console.error(e);
    }
  }, [reportStartDate, reportEndDate, reportEstado, reportPage, reportRows]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
  useEffect(() => { if (tab === 2) fetchReport(); }, [tab, fetchReport]);

  const refresh = () => {
    fetchDashboard();
    fetchCampaigns();
    if (tab === 2) fetchReport();
  };

  // ─── Campaign CRUD ───────────────────────────────────────

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (c: CashbackCampaign) => {
    setEditingId(c.id);
    setForm({
      name: c.name,
      description: c.description || '',
      tipo: c.tipo,
      percentagem: c.percentagem || undefined,
      valor_fixo: c.valor_fixo || undefined,
      max_por_transacao: c.max_por_transacao || undefined,
      max_por_utilizador: c.max_por_utilizador || undefined,
      max_utilizacoes_por_user: c.max_utilizacoes_por_user || undefined,
      start_date: c.start_date ? new Date(c.start_date).toISOString().slice(0, 16) : '',
      end_date: c.end_date ? new Date(c.end_date).toISOString().slice(0, 16) : '',
      budget_total: c.budget_total,
      funding_source: c.funding_source,
      funding_split_pagali: c.funding_split_pagali,
      funding_split_merchant: c.funding_split_merchant,
      restricao_uso: c.restricao_uso,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      if (editingId) {
        await cashbackService.updateCampaign(editingId, form);
        setSuccess('Campanha atualizada');
      } else {
        await cashbackService.createCampaign(form);
        setSuccess('Campanha criada');
      }
      setDialogOpen(false);
      fetchCampaigns();
      fetchDashboard();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await cashbackService.updateCampaignStatus(id, status);
      setSuccess(`Campanha ${status.toLowerCase()}`);
      fetchCampaigns();
      fetchDashboard();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSetupTables = async () => {
    try {
      await cashbackService.setupTables();
      setSuccess('Tabelas de cashback criadas/verificadas');
      refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSeedCampaigns = async () => {
    try {
      await cashbackService.seedCampaigns();
      setSuccess('Campanhas de exemplo criadas');
      fetchCampaigns();
      fetchDashboard();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ─── Render ──────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <CashbackIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Cashback
        </Typography>
        <Box>
          <Button size="small" onClick={handleSetupTables} sx={{ mr: 1 }}>
            Setup Tables
          </Button>
          <Button size="small" onClick={handleSeedCampaigns} sx={{ mr: 1 }}>
            Seed Campanhas
          </Button>
          <IconButton onClick={refresh}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ ml: 1 }}>
            Nova Campanha
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Dashboard Stats */}
      {dashboard && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Emitido', value: formatCurrency(dashboard.total_emitido), icon: <CashbackIcon />, color: '#1976d2' },
            { label: 'Confirmado', value: formatCurrency(dashboard.total_confirmado), icon: <TrendingIcon />, color: '#2e7d32' },
            { label: 'Utilizado', value: formatCurrency(dashboard.total_utilizado), icon: <TrendingIcon />, color: '#ed6c02' },
            { label: 'Expirado', value: formatCurrency(dashboard.total_expirado), icon: <TrendingIcon />, color: '#d32f2f' },
            { label: 'Custo Pagali', value: formatCurrency(dashboard.custo_pagali), icon: <CashbackIcon />, color: '#7b1fa2' },
            { label: 'Custo Comerciante', value: formatCurrency(dashboard.custo_comerciante), icon: <CashbackIcon />, color: '#0288d1' },
            { label: 'Utilizadores', value: String(dashboard.total_users), icon: <PeopleIcon />, color: '#388e3c' },
            { label: 'Campanhas Ativas', value: `${dashboard.campanhas_ativas} / ${dashboard.total_campanhas}`, icon: <CampaignIcon />, color: '#f57c00' },
          ].map((s, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                  <Typography variant="h6" fontWeight="bold">{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Campanhas" />
          <Tab label="Dashboard" />
          <Tab label={`Registos`} />
        </Tabs>
      </Paper>

      {/* Tab 0: Campaigns */}
      {tab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField size="small" label="Pesquisar" value={search}
              onChange={e => { setSearch(e.target.value); setCampaignPage(0); }} sx={{ minWidth: 200 }} />
            <TextField size="small" select label="Estado" value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setCampaignPage(0); }} sx={{ minWidth: 150 }}>
              <MenuItem value="">Todos</MenuItem>
              {['RASCUNHO', 'ATIVA', 'PAUSADA', 'TERMINADA', 'ESGOTADA'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>%/Valor</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Budget</TableCell>
                <TableCell>Usado</TableCell>
                <TableCell>Financiamento</TableCell>
                <TableCell>Periodo</TableCell>
                <TableCell>Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map(c => (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => openEdit(c)}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.tipo}</TableCell>
                  <TableCell>{c.tipo === 'PERCENTUAL' ? `${c.percentagem}%` : formatCurrency(c.valor_fixo || 0)}</TableCell>
                  <TableCell><Chip size="small" label={c.status} color={statusColor[c.status] || 'default'} /></TableCell>
                  <TableCell>{formatCurrency(c.budget_total)}</TableCell>
                  <TableCell>{formatCurrency(c.budget_used)} ({c.budget_total > 0 ? Math.round(c.budget_used / c.budget_total * 100) : 0}%)</TableCell>
                  <TableCell>{c.funding_source}</TableCell>
                  <TableCell>{formatDateParede(c.start_date)} — {formatDateParede(c.end_date)}</TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    {c.status === 'RASCUNHO' && (
                      <IconButton size="small" color="success" onClick={() => handleStatusChange(c.id, 'ATIVA')} title="Ativar">
                        <PlayIcon fontSize="small" />
                      </IconButton>
                    )}
                    {c.status === 'ATIVA' && (
                      <IconButton size="small" color="warning" onClick={() => handleStatusChange(c.id, 'PAUSADA')} title="Pausar">
                        <PauseIcon fontSize="small" />
                      </IconButton>
                    )}
                    {c.status === 'PAUSADA' && (
                      <IconButton size="small" color="success" onClick={() => handleStatusChange(c.id, 'ATIVA')} title="Retomar">
                        <PlayIcon fontSize="small" />
                      </IconButton>
                    )}
                    {['ATIVA', 'PAUSADA'].includes(c.status) && (
                      <IconButton size="small" color="error" onClick={() => handleStatusChange(c.id, 'TERMINADA')} title="Terminar">
                        <StopIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {campaigns.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center">Nenhuma campanha encontrada</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination component="div" count={campaignTotal} page={campaignPage}
            onPageChange={(_, p) => setCampaignPage(p)} rowsPerPage={campaignRows}
            onRowsPerPageChange={e => { setCampaignRows(+e.target.value); setCampaignPage(0); }} />
        </Paper>
      )}

      {/* Tab 1: Dashboard (already shown above as stats cards) */}
      {tab === 1 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Os stats globais estao apresentados acima. Graficos detalhados serao adicionados na Fase 2.
          </Typography>
        </Paper>
      )}

      {/* Tab 2: Report */}
      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField size="small" type="date" label="Data Inicio" value={reportStartDate}
              onChange={e => { setReportStartDate(e.target.value); setReportPage(0); }} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Data Fim" value={reportEndDate}
              onChange={e => { setReportEndDate(e.target.value); setReportPage(0); }} InputLabelProps={{ shrink: true }} />
            <TextField size="small" select label="Estado" value={reportEstado}
              onChange={e => { setReportEstado(e.target.value); setReportPage(0); }} sx={{ minWidth: 150 }}>
              <MenuItem value="">Todos</MenuItem>
              {['PENDENTE', 'CONFIRMADO', 'UTILIZADO', 'EXPIRADO', 'CANCELADO'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" startIcon={<DownloadIcon />}
              href={cashbackService.getExportUrl({ start_date: reportStartDate, end_date: reportEndDate })}
              target="_blank">
              Export CSV
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Campanha</TableCell>
                <TableCell>Transacao</TableCell>
                <TableCell>Cashback</TableCell>
                <TableCell>Financiamento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.id}</TableCell>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.user_id}</TableCell>
                  <TableCell>{r.campaign_name || r.campaign_id}</TableCell>
                  <TableCell>{formatCurrency(r.valor_transacao)}</TableCell>
                  <TableCell><strong>{formatCurrency(r.valor_cashback)}</strong></TableCell>
                  <TableCell>
                    {r.origem_financiamento}
                    {r.valor_pagali > 0 && <Box component="span" sx={{ fontSize: '0.75rem', ml: 0.5 }}>P:{formatCurrency(r.valor_pagali)}</Box>}
                    {r.valor_comerciante > 0 && <Box component="span" sx={{ fontSize: '0.75rem', ml: 0.5 }}>C:{formatCurrency(r.valor_comerciante)}</Box>}
                  </TableCell>
                  <TableCell><Chip size="small" label={r.estado} color={statusColor[r.estado] || 'default'} /></TableCell>
                  <TableCell>{formatDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
              {report.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">Sem registos</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination component="div" count={reportTotal} page={reportPage}
            onPageChange={(_, p) => setReportPage(p)} rowsPerPage={reportRows}
            onRowsPerPageChange={e => { setReportRows(+e.target.value); setReportPage(0); }} />
        </Paper>
      )}

      {/* Campaign Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Editar Campanha' : 'Nova Campanha de Cashback'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Nome da Campanha" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select label="Tipo" value={form.tipo}
                onChange={e => setForm({ ...form, tipo: e.target.value })}>
                <MenuItem value="PERCENTUAL">Percentual</MenuItem>
                <MenuItem value="VALOR_FIXO">Valor Fixo</MenuItem>
                <MenuItem value="PRIMEIRA_COMPRA">Primeira Compra</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Descricao" value={form.description || ''}
                onChange={e => setForm({ ...form, description: e.target.value })} />
            </Grid>

            {form.tipo === 'PERCENTUAL' && (
              <Grid item xs={6} sm={3}>
                <TextField fullWidth type="number" label="Percentagem (%)" value={form.percentagem || ''}
                  onChange={e => setForm({ ...form, percentagem: Number(e.target.value) })} />
              </Grid>
            )}
            {(form.tipo === 'VALOR_FIXO' || form.tipo === 'PRIMEIRA_COMPRA') && (
              <Grid item xs={6} sm={3}>
                <TextField fullWidth type="number" label="Valor Fixo (ECV)" value={form.valor_fixo || ''}
                  onChange={e => setForm({ ...form, valor_fixo: Number(e.target.value) })} />
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="number" label="Max/Transacao (ECV)" value={form.max_por_transacao || ''}
                onChange={e => setForm({ ...form, max_por_transacao: Number(e.target.value) || undefined })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="number" label="Max/Utilizador (ECV)" value={form.max_por_utilizador || ''}
                onChange={e => setForm({ ...form, max_por_utilizador: Number(e.target.value) || undefined })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="number" label="Max Utilizacoes/User" value={form.max_utilizacoes_por_user || ''}
                onChange={e => setForm({ ...form, max_utilizacoes_por_user: Number(e.target.value) || undefined })} />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="datetime-local" label="Inicio" value={form.start_date}
                onChange={e => setForm({ ...form, start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="datetime-local" label="Fim" value={form.end_date}
                onChange={e => setForm({ ...form, end_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="number" label="Budget Total (ECV)" value={form.budget_total}
                onChange={e => setForm({ ...form, budget_total: Number(e.target.value) })} required />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth select label="Financiamento" value={form.funding_source}
                onChange={e => setForm({ ...form, funding_source: e.target.value })}>
                <MenuItem value="PAGALI">Pagali</MenuItem>
                <MenuItem value="COMERCIANTE">Comerciante</MenuItem>
                <MenuItem value="PARTILHADO">Partilhado</MenuItem>
              </TextField>
            </Grid>

            {form.funding_source === 'PARTILHADO' && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth type="number" label="% Pagali" value={form.funding_split_pagali || ''}
                    onChange={e => setForm({ ...form, funding_split_pagali: Number(e.target.value) })} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth type="number" label="% Comerciante" value={form.funding_split_merchant || ''}
                    onChange={e => setForm({ ...form, funding_split_merchant: Number(e.target.value) })} />
                </Grid>
              </>
            )}

            <Grid item xs={6} sm={3}>
              <TextField fullWidth select label="Restricao Uso" value={form.restricao_uso || 'LIVRE'}
                onChange={e => setForm({ ...form, restricao_uso: e.target.value })}>
                <MenuItem value="LIVRE">Livre</MenuItem>
                <MenuItem value="RESTRITA">Restrita</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading || !form.name || !form.budget_total}>
            {editingId ? 'Guardar' : 'Criar Campanha'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CashbackPage;
